const SITE_NAME = 'UMKM Indonesia'

export type CatalogSeoBrand = {
  name: string
  slug: string
  description?: string | null
  logoUrl?: string | null
  address?: string | null
  whatsapp?: string | null
}

export function getCatalogPageUrl(slug: string) {
  return `${window.location.origin}/catalog/${slug}`
}

export function buildCatalogTitle(brandName: string) {
  return `${brandName} | Katalog Produk Online | ${SITE_NAME}`
}

export function buildCatalogDescription(brand: CatalogSeoBrand) {
  const base = `Lihat katalog produk ${brand.name}. Informasi produk, kontak WhatsApp, dan detail usaha tersedia secara online melalui ${SITE_NAME}.`
  if (brand.description?.trim()) {
    return `${base} ${brand.description.trim().slice(0, 80)}`.slice(0, 160)
  }
  return base.slice(0, 160)
}

function formatTel(whatsapp?: string | null) {
  if (!whatsapp) return undefined
  const digits = whatsapp.replace(/\D/g, '')
  if (!digits) return undefined
  if (digits.startsWith('62')) return `+${digits}`
  if (digits.startsWith('0')) return `+62${digits.slice(1)}`
  return `+${digits}`
}

function resolveImageUrl(url?: string | null) {
  if (!url) return undefined
  if (url.startsWith('http')) return url
  return `${window.location.origin}${url.startsWith('/') ? url : `/${url}`}`
}

export function buildCatalogJsonLd(brand: CatalogSeoBrand) {
  const pageUrl = getCatalogPageUrl(brand.slug)
  const image = resolveImageUrl(brand.logoUrl)
  const tel = formatTel(brand.whatsapp)

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': brand.address ? 'LocalBusiness' : 'Organization',
    name: brand.name,
    url: pageUrl,
    description: buildCatalogDescription(brand),
  }
  if (image) {
    schema.image = image
    schema.logo = image
  }
  if (tel) schema.telephone = tel
  if (brand.address) {
    schema.address = {
      '@type': 'PostalAddress',
      streetAddress: brand.address,
      addressCountry: 'ID',
    }
  }
  return schema
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = content
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.rel = 'canonical'
    document.head.appendChild(el)
  }
  el.href = href
}

function setJsonLd(data: Record<string, unknown>) {
  const id = 'catalog-json-ld'
  let el = document.getElementById(id) as HTMLScriptElement | null
  if (!el) {
    el = document.createElement('script')
    el.id = id
    el.type = 'application/ld+json'
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}

/** Pasang meta SEO dinamis di browser (melengkapi HTML crawler dari server) */
function getOgImageUrl(slug: string) {
  const apiBase = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '')
  return `${apiBase}/share/og/${slug}.png`
}

export function applyCatalogSeo(brand: CatalogSeoBrand, ogImageUrl?: string) {
  const pageUrl = getCatalogPageUrl(brand.slug)
  const title = buildCatalogTitle(brand.name)
  const description = buildCatalogDescription(brand)
  const image = ogImageUrl || getOgImageUrl(brand.slug) || resolveImageUrl(brand.logoUrl) || `${window.location.origin}/vite.svg`

  document.title = title
  setCanonical(pageUrl)
  setMeta('name', 'description', description)
  setMeta('name', 'robots', 'index, follow')
  setMeta('property', 'og:type', 'website')
  setMeta('property', 'og:site_name', SITE_NAME)
  setMeta('property', 'og:title', brand.name)
  setMeta('property', 'og:description', description)
  setMeta('property', 'og:url', pageUrl)
  setMeta('property', 'og:image', image)
  setMeta('name', 'twitter:card', 'summary_large_image')
  setMeta('name', 'twitter:title', brand.name)
  setMeta('name', 'twitter:description', description)
  setMeta('name', 'twitter:image', image)
  setJsonLd(buildCatalogJsonLd(brand))
}

export function clearCatalogSeo() {
  document.title = `${SITE_NAME} - Katalog Produk UMKM`
  document.getElementById('catalog-json-ld')?.remove()
}
